export type priority = 'none' | 'high' | 'medium' | 'low';
import type { RaptorSearchItem } from '@repo/common/types/Raptor';
import type { Readable, Writable } from 'svelte/store';
// FilteredType system interfaces and types
import type { ThreadConversation } from './Conversation';
import type { DraftType } from './Draft';
import type { UnionIconValues } from './Icon';
import type { Task } from './Task';
import type { Transcript } from './Transcript';
import type { UICalendarEvent } from './UICalendar';
import type { Workspace } from './Workspace';


// FilterSet type for task filtering
export interface FilterSet {
  // Multi-select filters (selecting none = selecting all)
  workspaceIds?: string[];
  tagIds?: string[];
  priorities?: priority[];
  calendarIds?: string[];
  memoryIds?: string[];
  driveTypes?: DraftType[];

  // Single-select filters
  datePreset?: DatePreset;

  // Boolean filters
  showCompleted?: boolean;
  showIncomplete?: boolean;
  isFavorite?: boolean;

  // Keyword search
  keyword?: KeywordFilter;

  iterator: number;
}

export interface DatePreset {
  type: 'all' | 'today' | 'upcoming';
  field: string; // e.g., 'dueAt' for tasks
}

export interface KeywordFilter {
  value: string;
  field: string; // e.g., 'title' for tasks
}

// Default filter set
export const createDefaultFilterSet = (): FilterSet => ({
  workspaceIds: [],
  tagIds: [],
  priorities: [],
  calendarIds: [],
  datePreset: {
    type: 'all',
    field: 'dueAt'
  },
  memoryIds: [],
  showCompleted: false,
  showIncomplete: true,
  isFavorite: false,
  keyword: {
    value: '',
    field: 'title'
  },
  iterator: 0
});

// Helper functions for filter operations
export const isMultiSelectEmpty = (values?: Set<string> | undefined): boolean => {
  return !values || values.size === 0;
};

export const isMultiSelectAll = (
  values?: Set<string> | undefined,
  allOptions?: string[]
): boolean => {
  if (!values || !allOptions) return true;
  return values.size === allOptions.length;
};

export const shouldApplyMultiSelectFilter = (
  values?: Set<string>,
  allOptions?: string[]
): boolean => {
  return !isMultiSelectEmpty(values) && !isMultiSelectAll(values, allOptions);
};

export const applyMultiSelectFilter = (
  testId?: string,
  values?: Set<string>,
  allOptions?: string[]
): boolean => {
  if (shouldApplyMultiSelectFilter(values, allOptions)) {
    return (testId !== undefined && values?.has(testId)) || false;
  } else {
    return true;
  }
};

export const applySimpleMultiSelectFilter = (
  testId: string | undefined,
  values?: Set<string>
): boolean => {
  if (!values?.size) {
    // Allow empty set to function as a wildcard
    return true;
  }
  if (testId === undefined) {
    return false;
  }
  return values.has(testId) || false;
};

export const createMultiSelectFilter = (
  values?: string[],
  allOptions?: string[]
): ((testId?: string) => boolean) => {
  const valuesSet = new Set(values);
  if (allOptions === undefined) {
    return (testId?: string) => applySimpleMultiSelectFilter(testId, valuesSet);
  }
  const allOptionsDeduped = Array.from(new Set(allOptions));
  return (testId?: string) => applyMultiSelectFilter(testId, valuesSet, allOptionsDeduped);
};

/**
 * Represents a drive item (document, note, etc.)
 */
export interface DriveItem {
  id: string;
  name: string;
  type: DraftType;
  lastModified: Date;
  workspaceId?: string;
  body?: string;
}

// Combined type for all filterable items
export type FilteredItemType =
  | Transcript
  | Task
  | UICalendarEvent
  | ThreadConversation
  | RaptorSearchItem
  | DriveItem;

// Filter values interface for the new filtering system
export interface FilterValues {
  tag: string[];
  space: string[];
  keyword: string;
  calendarId: string[];
  datePreset: string[];
  showCompleted: boolean;
  memory: string[];
}

// Section interface for consistent structure
export interface Section<T = any> {
  title: string;
  items: T[];
}

// Section configuration metadata
export interface SectionConfig {
  icon?: UnionIconValues;
  iconColor?: string;
  defaultCollapsed?: boolean;
  defaultDisplayLimit?: number;
  id?: string; // Unique identifier for state management
}

// Extended section with metadata
export interface SectionWithMetadata<T = any> extends Section<T> {
  config?: SectionConfig;
}

// Section state for tracking collapse/expand and pagination
export interface SectionState {
  collapsed: boolean;
  displayLimit: number;
}

// Filter option interface
export interface FilterOption {
  value: string;
  label: string;
  iconProps: { name: UnionIconValues };
  kind: 'default' | 'workspace' | 'calendar';
  workspace?: Workspace;
  providerId?: string;
  providerEmail?: string | null;
  calendar?: any;
}

// Filter interface for dynamic filter fields
export interface Filter {
  title: string;
  options: FilterOption[];
  counts: Record<string, number>;
  icon: UnionIconValues;
}

/**
 * Interface for the FilteredType public API.
 *
 * @template T - The input item type (must extend FilteredItemType)
 * @template U - The output item type for UI consumption (defaults to T)
 */
export interface IFilteredType<T extends FilteredItemType, U = T> {
  // Public readonly stores
  readonly filterTagValues: Writable<string[]>;
  readonly filterSpaceValues: Writable<string[]>;
  readonly filterKeywordValue: Writable<string>;
  readonly filterCalendarValues: Writable<string[]>;
  readonly filterDatePresetValues: Writable<string[]>;
  readonly filterFavoriteValue: Writable<boolean>;
  readonly filterShowCompletedValue: Writable<boolean>;
  readonly filterMemoryValues: Writable<string[]>;
  readonly limit: Writable<number>;
  readonly filteredItems: Readable<T[]>;
  readonly mappedItems: Readable<U[]>;
  readonly sections: Readable<Section<U>[]>;
  readonly sectionsWithState: Readable<SectionWithMetadata<U>[]>;
  readonly filters: Readable<Omit<Filter, 'counts'>[]>;
  readonly filtersWithCounts: Readable<Filter[]>;

  // Public getters
  readonly itemsStore: Readable<U[]>;
  readonly itemsPlain: U[];

  // Public methods
  increaseLimit(increment: number): void;
  getCurrentLimit(): number;
  getTotalFilteredCount(): number;
  enableFilterCountsCalculation(): void;
  areFilterCountsEnabled(): boolean;
  enableCountsForFilterTypes(filterTypes: string[]): void;
  disableCountsForFilterTypes(filterTypes: string[]): void;
  getEnabledCountTypes(): string[];
  getFilterStore(filterName: string): Writable<any> | undefined;
  setKeywordExternalIdSet(idSet: Set<string> | null): void;
  setKeywordExternalIdOrder(order: string[] | null): void;
  toggleSectionCollapsed(sectionId: string): void;
  updateSectionDisplayLimit(sectionId: string, newLimit: number): void;
  getSectionState(sectionId: string): SectionState;
}

export type FilteredTypeClass<T extends FilteredItemType, U = T> = new (
  items: Readable<T[]>,
  initialLimit?: number
) => IFilteredType<T, U>;
